import { useEffect, useRef, useState } from 'react';

import { createMediaErrorReporter } from '#features/media/reportMediaError';

/**
 * Живой уровень звука с микрофона.
 *
 * <p>Появился после разбора живой жалобы с прода: человек написал «my mics not working», а в
 * телеметрии его звонка разрешение стояло выданным, связь была без единой потери — и при этом за
 * 59 секунд ни он, ни собеседник не дали ни звука. Метка «доступа нет» такой случай не ловит:
 * доступ есть, звука нет. Разницу между «запрещено» и «разрешено, но молчит» видно только
 * измерением, поэтому оно и появилось.
 *
 * <p>Проверка до разговора открывает свой поток и закрывает его вместе с меню. В iOS-комнате
 * измеряем уже опубликованный трек: второй захват может помешать разговору. Заимствованный
 * трек измеритель никогда не останавливает, его жизненным циклом управляет владелец комнаты.
 */

/** Уровень из осциллограммы: среднеквадратичное отклонение от середины, приведённое к 0..1. */
export const levelFromWaveform = (data: Uint8Array): number => {
  if (data.length === 0) {
    return 0;
  }

  let sum = 0;
  for (const sample of data) {
    const deviation = (sample - 128) / 128;
    sum += deviation * deviation;
  }

  // Обычная речь даёт 0.05–0.2; множитель растягивает этот участок на всю полоску, иначе она
  // шевелится на пару пикселей и читается как «не работает».
  return Math.min(1, Math.sqrt(sum / data.length) * 5);
};

/** Ниже этого — тишина: дыхание и шум комнаты дают около 0.01. */
export const MIC_SPEECH_THRESHOLD = 0.08;

/**
 * Как часто отдавать новое значение наружу. Считаем каждый кадр — ради отзывчивости, — но пишем в
 * состояние реже: полоска живёт внутри открытого меню, и обновление 60 раз в секунду
 * перерисовывало бы весь список устройств столько же раз. 15 кадров в секунду для полоски уровня
 * неотличимы от шестидесяти, а работы это снимает вчетверо.
 */
const EMIT_INTERVAL_MS = 66;

/** Шевеление меньше этого глазом не видно — в состояние такое не пишем. */
const EMIT_DELTA = 0.02;

export type MicLevelState = {
  /** 0..1 для полоски. */
  level: number;
  /** Поток открыт, измерение идёт. */
  listening: boolean;
  /** Звук был услышан хоть раз за эту сессию измерения. */
  heard: boolean;
  /** Поток открыть не удалось — доступа нет или устройство занято. */
  failed: boolean;
};

const IDLE: MicLevelState = { level: 0, listening: false, heard: false, failed: false };

/** `track` borrows a conversation capture; null never starts a second capture. */
export const useMicLevel = (
  enabled: boolean,
  deviceId?: string,
  track?: MediaStreamTrack | null
): MicLevelState => {
  const [state, setState] = useState<MicLevelState>(IDLE);
  const heardRef = useRef(false);
  const mediaReporterRef = useRef(createMediaErrorReporter());

  useEffect(() => {
    if (
      !enabled ||
      track === null ||
      typeof navigator === 'undefined' ||
      (!track && !navigator.mediaDevices)
    ) {
      setState(IDLE);
      return;
    }

    let stopped = false;
    let stream: MediaStream | null = null;
    let ctx: AudioContext | null = null;
    let frame = 0;
    heardRef.current = false;

    const stop = () => {
      stopped = true;
      if (frame) {
        cancelAnimationFrame(frame);
      }
      if (!track) stream?.getTracks().forEach((item) => item.stop());
      document.removeEventListener('click', resume);
      void ctx?.close().catch(() => undefined);
    };

    const resume = () => {
      if (!stopped && ctx?.state === 'suspended') {
        void ctx.resume().catch((error: unknown) => {
          if (!stopped)
            mediaReporterRef.current(error, {
              scope: 'micLevel',
              operation: 'microphone-meter-resume',
              borrowedTrack: Boolean(track),
            });
        });
      }
    };

    const start = async () => {
      try {
        stream = track
          ? new MediaStream([track])
          : await navigator.mediaDevices.getUserMedia({
              audio: deviceId ? { deviceId: { exact: deviceId } } : true,
            });
        if (stopped) {
          if (!track) stream.getTracks().forEach((item) => item.stop());
          return;
        }

        const Ctor =
          window.AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctor) {
          return;
        }

        ctx = new Ctor();
        resume();
        document.addEventListener('click', resume);
        const analyser = ctx.createAnalyser();
        // Короткое окно: полоска должна реагировать на слог, а не на фразу.
        analyser.fftSize = 512;
        ctx.createMediaStreamSource(stream).connect(analyser);

        const buffer = new Uint8Array(analyser.fftSize);
        let lastEmitAt = 0;
        let lastLevel = -1;
        let lastHeard = false;

        const tick = () => {
          if (stopped) {
            return;
          }
          if (track?.readyState === 'ended') {
            setState({ level: 0, listening: false, heard: false, failed: true });
            stop();
            return;
          }
          if (track?.muted || track?.enabled === false) heardRef.current = false;
          analyser.getByteTimeDomainData(buffer);
          const level = track && (track.muted || !track.enabled) ? 0 : levelFromWaveform(buffer);
          if (level > MIC_SPEECH_THRESHOLD) {
            heardRef.current = true;
          }

          const now = performance.now();
          const changed =
            Math.abs(level - lastLevel) > EMIT_DELTA || heardRef.current !== lastHeard;
          if (changed && now - lastEmitAt >= EMIT_INTERVAL_MS) {
            lastEmitAt = now;
            lastLevel = level;
            lastHeard = heardRef.current;
            setState({ level, listening: true, heard: heardRef.current, failed: false });
          }

          frame = requestAnimationFrame(tick);
        };
        // Первый кадр отдаём сразу: до него полоска показывает «проверяем», и задержка в этом
        // месте читается как «не работает».
        setState({ level: 0, listening: true, heard: false, failed: false });
        tick();
      } catch (error) {
        if (!stopped) {
          mediaReporterRef.current(error, {
            scope: 'micLevel',
            operation: 'microphone-meter-start',
            borrowedTrack: Boolean(track),
          });
          setState({ level: 0, listening: false, heard: false, failed: true });
        }
      }
    };

    void start();
    return stop;
  }, [enabled, deviceId, track]);

  return state;
};
